import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  Grid,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  PlayCircleOutline as PlayIcon,
  Architecture as ArchitectureIcon,
  Settings as SettingsIcon,
  DataObject as DataObjectIcon,
  Analytics as AnalyticsIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Assignment as AssignmentIcon,
  AccountTree as AccountTreeIcon,
} from '@mui/icons-material';

interface LessonContentProps {
  onBack: () => void;
  onComplete: () => void;
  isCompleted: boolean;
}

const EUnifyVanguardLesson: React.FC<LessonContentProps> = ({
  onBack,
  onComplete,
  isCompleted,
}) => {
  const [activeStep, setActiveStep] = React.useState(0);

  const handleStepClick = (step: number) => {
    setActiveStep(step);
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleFinish = () => {
    onComplete();
  };

  const lessonSteps = [
    {
      label: "Introduction to E-Unify Vanguard Certification",
      content: (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" paragraph>
              E-Unify Vanguard is our elite certification program that validates comprehensive mastery across all aspects of the E-Unify platform. This certification represents the pinnacle of platform expertise and professional achievement.
            </Typography>
            <Typography variant="body1" paragraph>
              As an E-Unify Vanguard, you will demonstrate:
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon><ArchitectureIcon color="primary" /></ListItemIcon>
                <ListItemText primary="Deep Technical Expertise" secondary="Advanced understanding of platform architecture and optimization" />
              </ListItem>
              <ListItem>
                <ListItemIcon><DataObjectIcon color="primary" /></ListItemIcon>
                <ListItemText primary="Strategic Leadership" secondary="Design and implement enterprise-wide data intelligence solutions" />
              </ListItem>
              <ListItem>
                <ListItemIcon><SecurityIcon color="primary" /></ListItemIcon>
                <ListItemText primary="Governance Mastery" secondary="Expert application of data governance principles" />
              </ListItem>
              <ListItem>
                <ListItemIcon><AnalyticsIcon color="primary" /></ListItemIcon>
                <ListItemText primary="Community Leadership" secondary="Mentor others and advance best practices" />
              </ListItem>
            </List>
          </Box>
        </>
      ),
    },
    {
      label: "Certification Requirements",
      content: (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" paragraph>
              To earn your E-Unify Vanguard certification, you must meet these requirements:
            </Typography>
            <ol style={{ paddingLeft: '20px' }}>
              <li style={{ marginBottom: '8px' }}>
                <Typography variant="body1">
                  <strong>Complete Prerequisites:</strong> Business Glossary Training, Data Quality & Lineage Training
                </Typography>
              </li>
              <li style={{ marginBottom: '8px' }}>
                <Typography variant="body1">
                  <strong>Pass Assessment:</strong> Score 90% or higher on the comprehensive certification exam
                </Typography>
              </li>
              <li style={{ marginBottom: '8px' }}>
                <Typography variant="body1">
                  <strong>Time Commitment:</strong> 180 minutes (3 hours) for complete certification
                </Typography>
              </li>
              <li style={{ marginBottom: '8px' }}>
                <Typography variant="body1">
                  <strong>Hands-on Experience:</strong> Minimum 12 months working with E-Unify (recommended)
                </Typography>
              </li>
            </ol>
            <Alert severity="info" sx={{ mt: 2 }}>
              The certification is valid for 24 months and requires 20 hours of continuing education to maintain.
            </Alert>
          </Box>
        </>
      ),
    },
    {
      label: "Certification Benefits",
      content: (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" paragraph>
              Earning your E-Unify Vanguard certification provides significant professional benefits:
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Professional Recognition</Typography>
              <Typography variant="body2" paragraph>
                Elite badge, digital certificate, and recognition in the E-Unify Community directory
              </Typography>
              
              <Typography variant="subtitle1" sx={{ mb: 1, mt: 2, fontWeight: 'bold' }}>Career Advancement</Typography>
              <Typography variant="body2" paragraph>
                Demonstrated expertise for career progression and enhanced credibility with stakeholders
              </Typography>
              
              <Typography variant="subtitle1" sx={{ mb: 1, mt: 2, fontWeight: 'bold' }}>Community Impact</Typography>
              <Typography variant="body2" paragraph>
                Contribute to platform roadmap, participate in beta testing, and mentor emerging practitioners
              </Typography>
              
              <Typography variant="subtitle1" sx={{ mb: 1, mt: 2, fontWeight: 'bold' }}>Exclusive Access</Typography>
              <Typography variant="body2">
                Advanced feature previews, direct engagement with product experts, and Vanguard-only events
              </Typography>
            </Paper>
          </Box>
        </>
      ),
    },
    {
      label: "What You'll Learn",
      content: (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" paragraph>
              The Vanguard certification covers five comprehensive modules:
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Technical Mastery</Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><ArchitectureIcon color="primary" fontSize="small" /></ListItemIcon>
                        <ListItemText primary="Platform Architecture" secondary="Hub-and-spoke, microservices, security" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckCircleIcon color="primary" fontSize="small" /></ListItemIcon>
                        <ListItemText primary="Implementation" secondary="6-phase methodology, migration strategies" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><SpeedIcon color="primary" fontSize="small" /></ListItemIcon>
                        <ListItemText primary="Optimization" secondary="Performance tuning, troubleshooting" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Leadership Skills</Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><SecurityIcon color="primary" fontSize="small" /></ListItemIcon>
                        <ListItemText primary="Data Governance" secondary="Frameworks, quality management, compliance" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><AccountTreeIcon color="primary" fontSize="small" /></ListItemIcon>
                        <ListItemText primary="Community Leadership" secondary="Mentorship, knowledge sharing" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><AssignmentIcon color="primary" fontSize="small" /></ListItemIcon>
                        <ListItemText primary="Strategic Planning" secondary="Enterprise solutions, change management" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </>
      ),
    },
    {
      label: "Ready to Begin Your Journey?",
      content: (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" paragraph>
              The E-Unify Vanguard certification is your pathway to elite status in the data intelligence community:
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Complete 5 Comprehensive Lessons" 
                  secondary="180 minutes of advanced training content" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Pass the Certification Assessment" 
                  secondary="90% passing score required on 10-question exam" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Join the Vanguard Community" 
                  secondary="Connect with elite practitioners and thought leaders" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Earn Your Elite Badge" 
                  secondary="Display your expertise with professional certification" 
                />
              </ListItem>
            </List>
            <Alert severity="success" sx={{ mt: 2, mb: 3 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Ready to demonstrate your mastery and join the elite ranks of E-Unify Vanguard professionals?
              </Typography>
              <Button 
                variant="contained" 
                size="large"
                startIcon={<PlayIcon />}
                href="/vanguard"
                sx={{ 
                  bgcolor: 'secondary.main',
                  '&:hover': { bgcolor: '#8B1515' }
                }}
              >
                Start Vanguard Certification
              </Button>
            </Alert>
          </Box>
        </>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button 
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{ mr: 2 }}
        >
          Back to Lessons
        </Button>
        <Typography variant="h5">E-Unify Vanguard</Typography>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Elite Certification Program - Demonstrate Comprehensive Platform Mastery
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Duration: 20 minutes | Certification: 180 minutes
        </Typography>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Stepper activeStep={activeStep} orientation="vertical">
          {lessonSteps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel
                onClick={() => handleStepClick(index)}
                sx={{ cursor: 'pointer' }}
              >
                {step.label}
              </StepLabel>
              <StepContent>
                {step.content}
                <Box sx={{ mb: 2, mt: 3 }}>
                  <div>
                    <Button
                      disabled={index === 0}
                      onClick={handleBack}
                      sx={{ mr: 1 }}
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      onClick={
                        index === lessonSteps.length - 1
                          ? handleFinish
                          : handleNext
                      }
                      sx={{ mr: 1 }}
                    >
                      {index === lessonSteps.length - 1 ? 'Finish' : 'Next'}
                    </Button>
                  </div>
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Box>

      {isCompleted && (
        <Alert severity="success" sx={{ mt: 2 }}>
          You have completed this lesson. You can review the material or return to the lesson list.
        </Alert>
      )}
    </Box>
  );
};

export default EUnifyVanguardLesson;
